import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plane, Lock, ArrowRight, Loader2, FileText, Mail, MapPin } from "lucide-react";
import { ConsentCheckbox } from "@/components/ConsentCheckbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  { icon: FileText, text: "Personalised relocation assessment PDF — salary benchmark, suburb shortlist, cost-of-living comparison for your destination" },
  { icon: MapPin, text: "A clear package recommendation matched to your family, timeline and budget" },
  { icon: Mail, text: "Member access to The Ditch Collective monthly newsletter" },
];

export default function Assessment() {
  const params = new URLSearchParams(window.location.search);
  const [firstName, setFirstName] = useState(params.get("first_name") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please add your first name and a valid email.");
      return;
    }
    if (!consent) {
      toast.error("Please tick the acknowledgement to continue.");
      return;
    }
    setLoading(true);
    try {
      // Free assessment — route straight to the questionnaire, capturing identity.
      const params = new URLSearchParams({ first_name: firstName.trim(), email: email.trim() });
      window.location.href = `/questionnaire?${params.toString()}`;
    } catch {
      setLoading(false);
      toast.error("Couldn't start your assessment — please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[var(--cream)]" data-testid="assessment-page">
      <header className="px-5 md:px-8 py-5">
        <a href="/" className="inline-flex items-center gap-2 font-display font-extrabold text-[var(--ink)]">
          <Plane size={18} /> The Ditch Collective
        </a>
      </header>

      <main className="max-w-xl mx-auto px-4 pb-24">
        <span className="num-tag text-xs">Your relocation assessment</span>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold mt-2 leading-tight">
          Your move, <em className="font-serif">mapped out.</em>
        </h1>
        <p className="font-display font-bold text-xl text-[var(--ink)] mt-3" data-testid="assessment-price">
          Free <span className="text-sm font-normal text-[var(--ocean)]">· takes 6 minutes</span>
        </p>

        <div className="mt-7 space-y-4">
          {PERKS.map((p) => (
            <div key={p.text} className="flex items-start gap-3 bg-[var(--paper)] rounded-2xl border border-[var(--line)] p-4">
              <p.icon size={18} className="shrink-0 mt-0.5 text-[var(--lime-deep)]" />
              <p className="text-sm text-[var(--ink)] leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="mt-8 bg-[var(--paper)] rounded-3xl border border-[var(--line)] p-6 md:p-7 shadow-xl space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-[var(--ocean)] mb-1.5">First name</label>
            <input
              data-testid="assessment-first-name"
              className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-base focus:outline-none focus:border-[var(--ink)] transition-colors"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Your first name"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-[var(--ocean)] mb-1.5">
              Email — same one from your call booking
            </label>
            <input
              data-testid="assessment-email"
              type="email"
              className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-base focus:outline-none focus:border-[var(--ink)] transition-colors"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
            />
          </div>
          <ConsentCheckbox checked={consent} onChange={setConsent} testId="assessment-consent" />

          <button
            type="submit"
            data-testid="assessment-submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-[var(--ink)] text-[var(--paper)] font-display font-bold rounded-xl px-6 py-3.5 hover:bg-[var(--ink-soft)] transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : (<>Start my free assessment <ArrowRight size={16} /></>)}
          </button>
          <p className="flex items-center justify-center gap-1.5 text-xs text-[var(--ocean)]">
            <Lock size={12} /> Free · takes 6 minutes · do this before your call
          </p>
        </form>
      </main>
    </div>
  );
}
